import { Directive, Input, TemplateRef, ViewContainerRef, OnInit, ɵstringify as stringify } from '@angular/core';
import {PrincipalService} from '@core/auth/principal.service';
import {isNil} from '@shared/utils/utils';
import {User} from '@shared/models/user.model';

/**
 * @whatItDoes Conditionally exclude an HTML element if current user has any
 * of the authorities passed as the `expression`.
 *
 * @howToUse
 * ```
 *     <some-element *appHideFromAnyAuthority="'ROLE_ADMIN'">...</some-element>
 *
 *     <some-element *appHideFromAnyAuthority="['ROLE_ADMIN', 'ROLE_USER']">...</some-element>
 *
 *     <some-element *appHideFromAnyAuthority="['ROLE_ADMIN', 'ROLE_USER']; else elseBlock">...</some-element>
 *     <ng-template #elseBlock>...</ng-template>
 * ```
 */
@Directive({
    selector: '[appHideFromAnyAuthority]',
    standalone: false
})
export class HideFromAnyAuthorityDirective implements OnInit {

    private _authorities: string[];
    private _elseTemplateRef: TemplateRef<any>|null = null;

    constructor( private principal: PrincipalService,
                 private templateRef: TemplateRef<any>,
                 private viewContainerRef: ViewContainerRef) {
    }

    @Input()
    set appHideFromAnyAuthority(value: string|string[]) {
        this._authorities = typeof value === 'string' ? [ <string> value ] : <string[]> value;
    }

    @Input()
    set appHideFromAnyAuthorityElse (templateRef: TemplateRef<any>|null) {
        assertTemplate('appHasAnyAuthorityElse', templateRef);
        this._elseTemplateRef = templateRef;
        this._updateView();
    }

    ngOnInit(): void {
        this._updateView();
        // TODO: Is this next subscribe really needed
        // Get notified each time authentication state changes.
        // this.principal.getAuthenticationState().subscribe((identity) => this._updateView());
    }

    private _updateView(): void {
        this.principal.getActiveUser().subscribe( (user: User) => {
            this.viewContainerRef.clear();
            if (!this.principal.hasAnyAuthority(this._authorities)) {
                this.viewContainerRef.createEmbeddedView(this.templateRef);
            } else if (!isNil(this._elseTemplateRef)) {
                this.viewContainerRef.createEmbeddedView(this._elseTemplateRef);
            }
        });
    }
}

function assertTemplate(property: string, templateRef: TemplateRef<any>| null): void {
    const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);
    if (!isTemplateRefOrNull) {
      throw new Error(`${property} must be a TemplateRef, but received '${stringify(templateRef)}'.`);
    }
}
